import { useEffect, useRef, useState, type DragEvent } from "react";
import clsx from "clsx";
import { ApiError } from "@/lib/apiClient";
import { setGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";
import {
  deleteOrderWerkszeugnis,
  listOrderWerkszeugnisse,
  uploadOrderWerkszeugnis,
  type OrderHistoryLine,
  type OrderHistoryRow,
  type OrderWerkszeugnis,
} from "@/lib/orders";
import { orderLinePlainText, orderTextWithArticleNumber } from "@/lib/orderDraftUtils";
import { fmtOrderDateTime } from "@/pages/bestellungen/orderHistoryDisplay";
import { Button } from "@/components/ui/Button";

function lineLabel(line: OrderHistoryLine): string {
  const name =
    orderLinePlainText(orderTextWithArticleNumber(line.text || "", line.hero_nr)) || "Position";
  const lot = (line.lot_nummer || "").trim();
  const tk = (line.bestellnummer || "").trim();
  return [name, lot ? `LOT ${lot}` : "", tk].filter(Boolean).join(" · ");
}

export function OrderWerkszeugnisseSection({
  order,
  onOrderChange,
}: {
  order: OrderHistoryRow;
  onOrderChange: (order: OrderHistoryRow) => void;
}) {
  const items = Array.isArray(order.positionen) ? order.positionen : [];
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dropCounterRef = useRef(0);
  const ignoreClickAfterDropRef = useRef(false);
  const [files, setFiles] = useState<OrderWerkszeugnis[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [lineId, setLineId] = useState("");

  useEffect(() => {
    setGlobalUploadSuppressed(true);
    return () => setGlobalUploadSuppressed(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listOrderWerkszeugnisse(order.id)
      .then((res) => {
        if (cancelled) return;
        setFiles(res.items || []);
        if (Number(order.werkszeugnis_count || 0) !== res.count) {
          onOrderChange({ ...order, werkszeugnis_count: res.count });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Werkszeugnisse konnten nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Nur beim Öffnen bzw. Wechsel der Bestellung neu laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  function applyCount(count: number) {
    onOrderChange({ ...order, werkszeugnis_count: count });
  }

  async function onFilesSelected(list: FileList | null) {
    const selected = list ? Array.from(list) : [];
    if (!selected.length) return;
    setBusy(true);
    setError(null);
    setWarnings([]);
    const collected: string[] = [];
    try {
      const parsedLine = lineId ? Number(lineId) : null;
      for (const file of selected) {
        const res = await uploadOrderWerkszeugnis(order.id, file, parsedLine);
        collected.push(...(res.warnings || []));
      }
      const res = await listOrderWerkszeugnisse(order.id);
      setFiles(res.items || []);
      setWarnings(collected);
      applyCount(res.count);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Werkszeugnis konnte nicht abgelegt werden.");
      try {
        const res = await listOrderWerkszeugnisse(order.id);
        setFiles(res.items || []);
        applyCount(res.count);
      } catch {
        /* Liste bleibt wie zuvor */
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function hasDraggedFiles(event: React.DragEvent): boolean {
    return Array.from(event.dataTransfer?.types || []).includes("Files");
  }

  function onDragEnter(event: DragEvent) {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;
    dropCounterRef.current += 1;
    setDropActive(true);
  }

  function onDragOver(event: DragEvent) {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = busy ? "none" : "copy";
  }

  function onDragLeave(event: DragEvent) {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dropCounterRef.current = Math.max(0, dropCounterRef.current - 1);
    if (dropCounterRef.current === 0) setDropActive(false);
  }

  function onDrop(event: DragEvent) {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dropCounterRef.current = 0;
    setDropActive(false);
    ignoreClickAfterDropRef.current = true;
    if (busy) return;
    void onFilesSelected(event.dataTransfer.files);
  }

  async function removeFile(file: OrderWerkszeugnis) {
    if (!window.confirm(`„${file.filename}“ wirklich entfernen?`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await deleteOrderWerkszeugnis(order.id, file.id);
      setFiles((prev) => prev.filter((item) => item.id !== file.id));
      setWarnings(res.warnings || []);
      applyCount(res.werkszeugnis_count);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Werkszeugnis konnte nicht entfernt werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ios border border-separator bg-fill-quaternary/20 p-3 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-label">Werkszeugnisse</h3>
        <p className="text-xs text-label-secondary">
          Dateien liegen im InfoHub unter Werkszeugnisse im Ordner der TK-Nummer und zusätzlich im
          Projekt unter Lieferanten/Werkszeugnisse. Die LOT-Nummer ordnet den Artikel später zu.
        </p>
      </div>

      {items.length > 1 ? (
        <label className="block text-xs text-label-secondary">
          Zuordnung
          <select
            className="mt-0.5 block max-w-xs rounded-md border border-separator bg-card px-2 py-1 text-sm text-label"
            value={lineId}
            disabled={busy}
            onChange={(e) => setLineId(e.target.value)}
          >
            <option value="">Gesamte Bestellung</option>
            {items.map((line, idx) => (
              <option key={line.id ?? idx} value={line.id ?? ""} disabled={line.id == null}>
                {lineLabel(line)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp,.heic,application/pdf,image/*"
        multiple
        onChange={(e) => void onFilesSelected(e.target.files)}
      />
      <div
        role="button"
        data-werkszeugnis-drop=""
        tabIndex={busy ? -1 : 0}
        aria-disabled={busy}
        className={clsx(
          "flex min-h-[5.5rem] flex-col items-center justify-center rounded-ios border border-dashed px-4 py-4 text-center transition",
          busy ? "cursor-wait opacity-70" : "cursor-pointer",
          dropActive
            ? "border-accent bg-accent/10 text-label"
            : "border-separator text-label-secondary hover:border-accent hover:bg-accent/5",
        )}
        onClick={() => {
          if (ignoreClickAfterDropRef.current) {
            ignoreClickAfterDropRef.current = false;
            return;
          }
          if (!busy) fileRef.current?.click();
        }}
        onKeyDown={(event) => {
          if (busy) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            fileRef.current?.click();
          }
        }}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <p className="text-sm text-label">
          {busy ? "Werkszeugnis wird abgelegt …" : "Werkszeugnisse hierher ziehen"}
        </p>
        <p className="mt-1 text-xs text-label-tertiary">oder klicken zum Auswählen · PDF oder Bild</p>
      </div>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {warnings.length > 0 ? (
        <ul className="space-y-1 text-xs text-status-orange">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Werkszeugnisse werden geladen …</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-label-tertiary">Noch kein Werkszeugnis abgelegt.</p>
      ) : (
        <ul className="divide-y divide-separator rounded-ios border border-separator bg-card">
          {files.map((file) => (
            <li key={file.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
              <span className="min-w-0 flex-1">
                <span className="font-medium text-label">{file.filename}</span>
                {file.lot_nummer ? (
                  <span className="ml-2 font-mono text-xs text-label-secondary">LOT {file.lot_nummer}</span>
                ) : null}
                <span className="mt-0.5 block text-xs text-label-tertiary">
                  {file.uploaded_at ? fmtOrderDateTime(file.uploaded_at) : ""}
                  {file.uploaded_by ? ` · ${file.uploaded_by}` : ""}
                </span>
              </span>
              <span className="flex flex-wrap gap-2">
                {file.links
                  .filter((link) => link.web_url)
                  .map((link) => (
                    <a
                      key={`${file.id}-${link.kind}-${link.label}`}
                      href={link.web_url || undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-accent hover:underline"
                    >
                      {link.label}
                    </a>
                  ))}
              </span>
              <Button
                type="button"
                size="sm"
                variant="danger"
                disabled={busy}
                onClick={() => void removeFile(file)}
              >
                Entfernen
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
